import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TEAM } from "@/data/teamMembers";
import CTABand from "@/components/CTABand";
import { useSeo } from "@/lib/seo";
import { useReveal } from "@/lib/reveal";

function Avatar({ member }) {
  if (member.photoUrl) {
    return (
      <img
        src={member.photoUrl}
        alt={`${member.name} portrait`}
        className="h-24 w-24 rounded-full object-cover ring-4 ring-white shadow-card"
      />
    );
  }
  return (
    <div
      className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-ra-ocean to-ra-oceanLight text-white shadow-card ring-4 ring-white"
      aria-label={`${member.name} avatar placeholder`}
    >
      <span className="font-display text-2xl font-semibold tracking-tight">{member.initials}</span>
    </div>
  );
}

export default function About() {
  useSeo({
    title: "About · Operator-built rental software",
    description:
      "RentAnything.ai was built by operators who ran rental businesses and got tired of bad software. Meet the team behind the multi-vertical booking platform.",
    path: "/about",
  });
  useReveal();

  return (
    <>
      {/* HERO */}
      <section className="ra-mesh overflow-hidden">
        <div className="ra-container py-20 sm:py-28">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
            <div>
              <span className="ra-pill"><span className="h-1.5 w-1.5 rounded-full bg-ra-amber" /> Built by operators</span>
              <h1 className="mt-5 ra-h1" data-testid="about-headline">
                <span className="text-ra-oceanDeep">EVERY</span> rental business is unique <span className="whitespace-nowrap">— we know that.</span>
              </h1>
              <p className="mt-5 ra-lead">
                At RentAnything.ai, we understand that every rental operation is unique — because we operate rental businesses too. From flexible booking calendars to complex inventory management, we've designed a platform that adapts to your needs. Whether you're managing rentals, ticketed tours, or one-time events, our software simplifies the entire process. Need to adjust a reservation, issue refunds, send reminders, or manage multiple locations? We've got you covered. RentAnything.ai is the all-in-one solution to streamline your business, improve efficiency, and give you the flexibility to focus on what matters most — your customers.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/contact" className="ra-btn-primary" data-testid="about-demo-btn">
                  Talk to the team <ArrowRight size={16} />
                </Link>
                <Link to="/product" className="ra-btn-ghost">See the platform</Link>
              </div>
            </div>
            <div className="relative reveal">
              <div className="rounded-3xl border border-ra-line bg-white p-8 shadow-card">
                <span className="ra-eyebrow">Mission</span>
                <p className="mt-4 font-display text-2xl font-semibold leading-snug text-ra-ink">
                  Make the world's best rental businesses possible — across every vertical, on one configurable engine.
                </p>
                <p className="mt-4 text-sm text-ra-ink/70">
                  Marine first because we know it cold. Then equipment, recreation, powersports, tours, events. Then the verticals we haven't met yet.
                </p>
              </div>
              <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-ra-amber/25 blur-2xl" />
              <div className="pointer-events-none absolute -left-6 -bottom-6 h-28 w-28 rounded-full bg-ra-ocean/25 blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="ra-section">
        <div className="ra-container">
          <div className="max-w-2xl reveal">
            <span className="ra-eyebrow">The team</span>
            <h2 className="mt-3 ra-h2">Operators, builders, and one very patient project manager.</h2>
            <p className="mt-4 ra-lead">
              The people behind RentAnything — and the operator-grade obsession behind the product.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TEAM.map((m) => (
              <article key={m.name} className="ra-card reveal" data-testid={`team-card-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center gap-4">
                  <Avatar member={m} />
                  <div>
                    <h3 className="font-display text-lg font-semibold text-ra-ink leading-tight">{m.name}</h3>
                    <p className="text-xs font-semibold uppercase tracking-wider text-ra-ocean">{m.title}</p>
                  </div>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-ra-ink/75">{m.bio}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="ra-section bg-ra-paper border-y border-ra-line">
        <div className="ra-container">
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              { k: "Operator-first", v: "Every feature has to survive a Saturday at peak season." },
              { k: "Flexibility over scope", v: "We'd rather model fewer rules better than ship 20 half-built workflows." },
              { k: "Plainspoken", v: "Software that doesn't waste your time. Marketing that doesn't either." },
            ].map((p) => (
              <div key={p.k} className="ra-card reveal">
                <p className="ra-eyebrow !text-ra-ink/50 !text-[10px]">Principle</p>
                <h3 className="mt-2 font-display text-lg font-semibold text-ra-ink">{p.k}</h3>
                <p className="mt-2 text-sm text-ra-ink/75">{p.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        eyebrow="Want to chat?"
        title="Talk to a builder, not a sales rep."
        sub="The fastest way to know if RentAnything fits is a 30-minute call with someone who actually built it."
        primaryLabel="Talk to the team"
      />

      {/* MADE IN strip — verbatim from existing rentanything.ai */}
      <div className="border-t border-ra-line bg-white" data-testid="about-made-in-strip">
        <div className="ra-container py-6">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.32em] text-ra-ink/55">
            MADE IN NC <span className="text-ra-coral">✦</span> MADE IN SC <span className="text-ra-coral">✦</span> MADE IN MI <span className="text-ra-coral">✦</span>
          </p>
        </div>
      </div>
    </>
  );
}
